import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
// You need to install react-dnd and react-dnd-html5-backend for this to work
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Loader from "../components/Loader";
import { toast } from "react-toastify";
import DeleteModal from "../components/DeleteModal.jsx/DeleteModal";

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date)) return "-";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const ItemType = "STAFF_ROW";

function DraggableRow({ user, index, moveRow, ...props }) {
  const ref = useRef(null);
  const [, drop] = useDrop({
    accept: ItemType,
    hover(item) {
      if (item.index === index) return;
      moveRow(item.index, index);
      item.index = index;
    },
  });
  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemType,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drag(drop(ref));
  return (
    <tr
      ref={ref}
      style={{ opacity: isDragging ? 0.5 : 1, cursor: "move" }}
      {...props}
    >
      <td style={{ width: 32, textAlign: "center", cursor: "grab" }}>
        <span style={{ fontSize: 18, color: "#888" }} title="Drag to reorder">
          ≡
        </span>
      </td>
      {props.children}
    </tr>
  );
}

const Staff = () => {
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [staffId, setStaffId] = useState(null);
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [totalRecords, setTotalRecords] = useState(0);
  const [draw, setDraw] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const cursorPositionRef = useRef(0);

  // Map frontend sort field to backend column index
  const getSortColumnIndex = (field) => {
    const mapping = {
      "id": 0,
      "order": 1,
      "name": 2,
      "email": 3,
      "role": 4,
      "department": 5,
      "guardian_page": 6,
      "property_owners": 7,
      "about_us": 8,
      "calendar_color": 9,
      "last_login": 10,
      "created_at": 11
    };
    return mapping[field] || 0;
  };

  const fetchStaff = useCallback(() => {
    console.log("🔍 [FRONTEND] Fetching staff with server-side pagination");
    // Don't show loading spinner if user is typing (to prevent focus loss)
    if (!isSearchFocused) {
      setLoading(true);
    }

    const startIndex = (currentPage - 1) * pageSize;
    const datatableParams = {
      draw: draw,
      start: startIndex,
      length: pageSize,
      search: { value: debouncedSearch }, // Use debouncedSearch instead of search
      order: [{ column: getSortColumnIndex(sortField), dir: sortDirection }]
    };

    console.log("📊 [FRONTEND] Staff DataTables params:", datatableParams);

    fetch(`${process.env.REACT_APP_API_URL}/api/users/staff/datatable`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem("token")}`
      },
      body: JSON.stringify(datatableParams)
    })
      .then((res) => res.json())
      .then((data) => {
        console.log("📊 [FRONTEND] Staff DataTables response:", data);
        setStaff(Array.isArray(data.data) ? data.data : []);
        setTotalRecords(data.recordsFiltered || 0);
        setDraw(data.draw);
        setLoading(false);
      })
      .catch((error) => {
        console.error("❌ [FRONTEND] Error fetching staff:", error);
        setStaff([]);
        setTotalRecords(0);
        setLoading(false);
      });
  }, [currentPage, pageSize, debouncedSearch, sortField, sortDirection, draw, isSearchFocused]);

  // Debounce search input - update debouncedSearch after user stops typing
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If search is empty, update immediately (no debounce needed for clearing)
    if (search === "") {
      setDebouncedSearch("");
      setCurrentPage(1);
      return;
    }

    // Set new timeout to update debouncedSearch after 300ms
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1); // Reset to first page when search changes
    }, 300);

    // Cleanup timeout on unmount or when search changes
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search]);

  // Maintain focus on search input after re-renders
  useEffect(() => {
    if (isSearchFocused && searchInputRef.current) {
      // Use requestAnimationFrame for better timing
      requestAnimationFrame(() => {
        if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
          searchInputRef.current.focus();
          // Restore cursor position
          const cursorPos = cursorPositionRef.current || search.length;
          try {
            searchInputRef.current.setSelectionRange(cursorPos, cursorPos);
          } catch (e) {
            // Some inputs don't support setSelectionRange
            console.log("Could not set cursor position:", e);
          }
        }
      });
    }
  });

  // Fetch staff when debouncedSearch or other dependencies change
  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    // Save cursor position before state update
    if (e.target.selectionStart !== null) {
      cursorPositionRef.current = e.target.selectionStart;
    }
    setSearch(value);
    // Don't reset currentPage here - it will be reset in the debounce useEffect
    // This prevents multiple state updates and re-renders
  };

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
  };

  const handleSearchBlur = () => {
    setIsSearchFocused(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Pagination calculations for server-side
  const totalPages = Math.ceil(totalRecords / pageSize);

  // Render pagination controls (matching Laravel DataTables style)
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxPagesToShow = 5; // Show max 5 page numbers at a time
    const halfRange = Math.floor(maxPagesToShow / 2);

    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);

    // Adjust if we're near the beginning or end
    if (currentPage <= halfRange) {
      endPage = Math.min(maxPagesToShow, totalPages);
    }
    if (currentPage + halfRange >= totalPages) {
      startPage = Math.max(1, totalPages - maxPagesToShow + 1);
    }

    // Add first page and ellipsis if needed
    if (startPage > 1) {
      pages.push(
        <li key={1} className={currentPage === 1 ? 'active' : ''}>
          <a href="#" onClick={(e) => { e.preventDefault(); handlePageChange(1); }}>
            1
          </a>
        </li>
      );
      if (startPage > 2) {
        pages.push(
          <li key="ellipsis-start" className="disabled">
            <span>...</span>
          </li>
        );
      }
    }

    // Add page numbers in range
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={currentPage === i ? 'active' : ''}>
          <a href="#" onClick={(e) => { e.preventDefault(); handlePageChange(i); }}>
            {i}
          </a>
        </li>
      );
    }

    // Add ellipsis and last page if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <li key="ellipsis-end" className="disabled">
            <span>...</span>
          </li>
        );
      }
      pages.push(
        <li key={totalPages} className={currentPage === totalPages ? 'active' : ''}>
          <a href="#" onClick={(e) => { e.preventDefault(); handlePageChange(totalPages); }}>
            {totalPages}
          </a>
        </li>
      );
    }

    return (
      <div className="dataTables_paginate">
        <ul className="pagination">
          <li className={currentPage === 1 ? 'disabled' : ''}>
            <a href="#" onClick={(e) => { e.preventDefault(); if (currentPage > 1) handlePageChange(currentPage - 1); }}>
              Previous
            </a>
          </li>
          {pages}
          <li className={currentPage === totalPages ? 'disabled' : ''}>
            <a href="#" onClick={(e) => { e.preventDefault(); if (currentPage < totalPages) handlePageChange(currentPage + 1); }}>
              Next
            </a>
          </li>
        </ul>
      </div>
    );
  };

  const handleShow = (id) => navigate(`/users/${id}`);
  const handleEdit = (id) => navigate(`/users/${id}/edit`);
  const handleCalendar = (id) => navigate(`/property_viewings/user/${id}`);
  const handleRemove = (id) => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/api/users/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.requiresConfirmation) {
          // Show confirmation dialog with related records info

          // User confirmed, make the confirmed deletion request
          fetch(data.confirmUrl, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          })
            .then((res) => res.json())
            .then((deleteData) => {
              if (deleteData.message) {
                fetchStaff();
                alert(deleteData.message);
              } else {
                alert("Failed to delete user");
                setLoading(false);
              }
            })
            .catch((error) => {
              setLoading(false);
              console.error("Error deleting user:", error);
              toast.error("Failed to delete user");
            });
        } else if (data.message) {
          fetchStaff();
          toast.success("Staff deleted successfully");
          setIsOpenDeleteModal(false);
          setLoading(true);
        } else {
          alert("Failed to delete user");
        }
      })
      .catch((error) => {
        console.error("Error deleting user:", error);
        alert("Failed to delete user");
      });
  };

  // Drag-and-drop logic (for current page only)
  const moveRow = (from, to) => {
    const updated = [...staff];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    setStaff(updated);

    // Save order to backend
    const order = updated.map((u, idx) => ({
      users_id: u.users_id,
      position: idx,
    }));
    fetch(`${process.env.REACT_APP_API_URL}/api/users/staff/order`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({ order }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.message === "Staff order updated") {
          setNotification({ type: "success", message: "Order saved!" });
        } else {
          setNotification({
            type: "error",
            message: data.message || "Failed to save order",
          });
        }
        setTimeout(() => setNotification(null), 2000);
      })
      .catch(() => {
        setNotification({ type: "error", message: "Failed to save order" });
        setTimeout(() => setNotification(null), 2000);
      });
  };

  if (loading) return <Loader />;

  return (
    <div className="container-fluid users-page">
      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          onClick={() => navigate('/dashboard')}
          style={{ 
            fontWeight: 400, 
            fontSize: "14px", 
            color: "black",
            cursor: 'pointer'
          }}
        >
          Dashboard
        </span>
        <span className="breadcrumb-sep" style={{ margin: "0 8px" }}>/</span>
        <span
          className="breadcrumb-main"
          onClick={() => navigate('/users')}
          style={{ 
            fontWeight: 400, 
            fontSize: "14px", 
            color: "black",
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
        >
          Users
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Staff</span>
        </div>
      </div>
      <DeleteModal
        isOpen={isOpenDeleteModal}
        onClose={() => setIsOpenDeleteModal(false)}
        onConfirm={() => handleRemove(staffId)}
        isLoading={loading}
      />
      <div className="users-overview-container">
        <div className="staff-list-title">Staff</div>
        {notification && (
          <div
            style={{
              background:
                notification.type === "success" ? "#d4edda" : "#f8d7da",
              color: notification.type === "success" ? "#155724" : "#721c24",
              border: `1px solid ${
                notification.type === "success" ? "#c3e6cb" : "#f5c6cb"
              }`,
              padding: "8px 16px",
              borderRadius: 4,
              marginBottom: 16,
              maxWidth: 400,
            }}
          >
            {notification.message}
          </div>
        )}
        <div className="users-table-toolbar">
          <div className="users-table-entries">
            Show
            <select
              className="entries-select"
              value={pageSize}
              onChange={handlePageSizeChange}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            entries
          </div>
          <div className="users-table-search">
            Search:
            <input
              ref={searchInputRef}
              type="text"
              className="form-control"
              placeholder=""
              value={search}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              autoComplete="off"
            />
          </div>
        </div>
        <div className="users-table-wrapper">
          <DndProvider backend={HTML5Backend}>
            <table className="users-table">
              <thead>
                <tr>
                  <th 
                    className="sortable"
                    onClick={() => handleSort("id")}
                    style={{ cursor: "pointer" }}
                  >
                    #<SortIcon direction={sortField === "id" ? sortDirection : null} isActive={sortField === "id"} />
                  </th>
                  <th 
                    className="sortable"
                    onClick={() => handleSort("order")}
                    style={{ cursor: "pointer" }}
                  >
                    Order<SortIcon direction={sortField === "order" ? sortDirection : null} isActive={sortField === "order"} />
                  </th>
                  <th 
                    className="sortable"
                    onClick={() => handleSort("name")}
                    style={{ cursor: "pointer" }}
                  >
                    Name<SortIcon direction={sortField === "name" ? sortDirection : null} isActive={sortField === "name"} />
                  </th>
                  <th 
                    className="sortable"
                    onClick={() => handleSort("email")}
                    style={{ cursor: "pointer" }}
                  >
                    E-mail<SortIcon direction={sortField === "email" ? sortDirection : null} isActive={sortField === "email"} />
                  </th>
                  <th 
                    className="sortable"
                    onClick={() => handleSort("role")}
                    style={{ cursor: "pointer" }}
                  >
                    Role<SortIcon direction={sortField === "role" ? sortDirection : null} isActive={sortField === "role"} />
                  </th>
                  <th 
                    className="sortable"
                    onClick={() => handleSort("department")}
                    style={{ cursor: "pointer" }}
                  >
                    Department<SortIcon direction={sortField === "department" ? sortDirection : null} isActive={sortField === "department"} />
                  </th>
                  <th 
                    className="sortable"
                    onClick={() => handleSort("guardian_page")}
                    style={{ cursor: "pointer" }}
                  >
                    Guardian Page<SortIcon direction={sortField === "guardian_page" ? sortDirection : null} isActive={sortField === "guardian_page"} />
                  </th>
                  <th 
                    className="sortable"
                    onClick={() => handleSort("property_owners")}
                    style={{ cursor: "pointer" }}
                  >
                    Property Owners<SortIcon direction={sortField === "property_owners" ? sortDirection : null} isActive={sortField === "property_owners"} />
                  </th>
                  <th 
                    className="sortable"
                    onClick={() => handleSort("about_us")}
                    style={{ cursor: "pointer" }}
                  >
                    About us<SortIcon direction={sortField === "about_us" ? sortDirection : null} isActive={sortField === "about_us"} />
                  </th>
                  <th 
                    className="sortable"
                    onClick={() => handleSort("calendar_color")}
                    style={{ cursor: "pointer" }}
                  >
                    Calendar Color<SortIcon direction={sortField === "calendar_color" ? sortDirection : null} isActive={sortField === "calendar_color"} />
                  </th>
                  <th 
                    className="sortable"
                    onClick={() => handleSort("last_login")}
                    style={{ cursor: "pointer" }}
                  >
                    Last login<SortIcon direction={sortField === "last_login" ? sortDirection : null} isActive={sortField === "last_login"} />
                  </th>
                  <th 
                    className="sortable"
                    onClick={() => handleSort("created_at")}
                    style={{ cursor: "pointer" }}
                  >
                    Created at<SortIcon direction={sortField === "created_at" ? sortDirection : null} isActive={sortField === "created_at"} />
                  </th>
                  <th className="action-head">Actions</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((u, idx) => (
                  <DraggableRow
                    key={u.users_id || idx}
                    user={u}
                    index={idx}
                    moveRow={moveRow}
                  >
                    {/* <td /> */}
                    <td>{idx + 1}</td>
                    {/* <td>{u.staff_order}</td> */}
                    <td>
                      <a
                        href="#"
                        className="text-primary user-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handleShow(u.users_id);
                        }}
                      >
                        {`${u.users_first_name || ""} ${
                          u.users_last_name || ""
                        }`.trim()}
                      </a>
                    </td>
                    <td>{u.users_email}</td>
                    <td>{u.roleName || "-"}</td>
                    <td>{u.departmentName || "-"}</td>
                    <td>{u.staff_guardian_page ? "Yes" : "No"}</td>
                    <td>{u.staff_property_owners ? "Yes" : "No"}</td>
                    <td>{u.staff_about_us_page ? "Yes" : "No"}</td>
                    <td>
                      {u.users_color ? (
                        <span
                          className="label"
                          style={{
                            backgroundColor: u.users_color,
                            color: "#fff",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontSize: "0.85em",
                          }}
                        >
                          {u.users_color}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{formatDate(u.users_last_login)}</td>
                    <td>{formatDate(u.users_created_at)}</td>
                    <td>
                      <button
                        className="btn-action show"
                        data-userid={u.users_id}
                        onClick={() => handleShow(u.users_id)}
                      >
                        Show
                      </button>
                      <button
                        className="btn-action edit"
                        data-userid={u.users_id}
                        onClick={() => handleEdit(u.users_id)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn-action remove"
                        data-userid={u.users_id}
                        onClick={() => {
                          setStaffId(u.users_id);
                          setIsOpenDeleteModal(true);
                        }}
                      >
                        Remove
                      </button>
                      <button
                        className="btn-action calendar"
                        data-userid={u.users_id}
                        onClick={() => handleCalendar(u.users_id)}
                      >
                        Viewing Calendar
                      </button>
                    </td>
                  </DraggableRow>
                ))}
              </tbody>
            </table>
          </DndProvider>

          {/* Pagination Info and Controls */}
          <div className="dataTables_info_wrapper" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
            <div className="dataTables_info">
              Showing {totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1} to{" "}
              {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} entries
            </div>
            {renderPagination()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Staff;
